import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { FileText, Users, Plus, Link2 } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import Breadcrumbs, { breadcrumbLabel } from '@/components/Breadcrumbs';
import ScopedSubmissionsTable from '@/pages/Submissions/components/tabs/ScopedSubmissionsTable';
import StudyActivity from '@/pages/Studies/components/StudyActivity';
import { useStudy } from '@/hooks/studies/useStudy';
import ConnectSiteModal from '@/pages/Studies/components/ConnectSiteModal';
import BulkInviteUsersModal from '@/pages/Submissions/components/BulkInviteUsersModal';
import UserManagement from '@/pages/Submissions/components/tabs/UserManagement';
import ScopedSitesTable from '@/pages/Submissions/components/ScopedSitesTable';
import StudyDocuments from '@/pages/Studies/components/StudyDocuments';
import StudyFinalDocuments from '@/pages/Studies/components/StudyFinalDocuments';
import { getEntityStatusInfo } from '@/utils/entityStatuses';
import { useSitesList } from '@/hooks/sites/useSitesList';

// Tab ids that have been renamed, mapped old → new so existing bookmarks and
// emailed links keep working. `packets` became `final-documents` when the
// packet was retired as a customer-facing concept.
const LEGACY_TAB_IDS: Record<string, string> = {
  packets: 'final-documents',
};

// Rows per page for the tables embedded in this page's tabs. Deliberately
// small: a study tab stacks up to two tables plus their toolbars, and an
// unbounded list pushed the rest of the page (and the other table) off screen.
// Only the study tabs pass this — the Site and Submission show pages render
// the same components unpaginated.
const STUDY_TAB_PAGE_SIZE = 5;

const StudyShow: React.FC = () => {
  const { studyId } = useParams<{ studyId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: study, isLoading, error } = useStudy(studyId!);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [isConnectSiteModalOpen, setIsConnectSiteModalOpen] = useState(false);
  const [usersRefreshTrigger, setUsersRefreshTrigger] = useState(0);

  const { data: sites = [] } = useSitesList({
    studyId: studyId!,
    order: 'desc',
    sort: 'created_at',
  });

  const handleCreateSubmission = useCallback(() => {
    navigate(`/submissions/new?owner_id=${studyId}`);
  }, [navigate, studyId]);

  const handleCreateSite = useCallback(() => {
    navigate(`/sites/new?owner_id=${studyId}`);
  }, [navigate, studyId]);

  const handleInviteUsers = useCallback(() => {
    setIsInviteModalOpen(true);
  }, []);

  const handleCloseInviteModal = useCallback(() => {
    setIsInviteModalOpen(false);
  }, []);

  const handleInvitationSuccess = useCallback(() => {
    setTimeout(() => {
      setUsersRefreshTrigger(prev => prev + 1);
    }, 500);
  }, []);

  const handleConnectSite = useCallback(() => {
    setIsConnectSiteModalOpen(true);
  }, []);

  const handleCloseConnectSiteModal = useCallback(() => {
    setIsConnectSiteModalOpen(false);
  }, []);

  // A successful connect/attach can flip this study's accepts_more_sites (e.g. a
  // single-site study just filled its one slot) and always changes its site list —
  // both are cached with a non-zero staleTime, so without this, navigating away and
  // back shows stale data (New Site / Invite or Attach Site still appear) until the
  // cache naturally expires or a hard refresh forces a refetch.
  const handleConnectSiteSuccess = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['study', studyId] });
    queryClient.invalidateQueries({ queryKey: ['sites'] });
  }, [queryClient, studyId]);

  useEffect(() => {
    if (study) {
      const title =
        study.display_id ||
        study.protocol_number ||
        study.protocol_title ||
        'Study Details';
      document.title = `${title} - OneVerse`;
    }

    return () => {
      document.title = 'OneVerse';
    };
  }, [study, studyId]);

  const canManageUsers = study?.can_manage_users ?? false;
  const variant = study?.submission_template?.variant;

  // A Single Site Protocol study never has a StudySite — its site details
  // live on the study's own initial submission — so the Sites tab has
  // nothing to show and is never rendered for one, regardless of sites.length.
  const isSingleSite = variant === 'Single Site Protocol';

  const tabs = useMemo(
    () => [
      { id: 'submissions', label: 'Submissions' },
      ...(!isSingleSite && sites.length > 0
        ? [{ id: 'sites', label: 'Sites' }]
        : []),
      { id: 'documents', label: 'Submitted Documents' },
      { id: 'final-documents', label: 'Final Documents' },
      ...(canManageUsers ? [{ id: 'users', label: 'Users' }] : []),
      { id: 'activity', label: 'Activity' },
    ],
    [canManageUsers, sites.length, isSingleSite]
  );

  // The tab id travels in the shareable URL, so it is customer-facing copy in
  // its own right — `?tab=packets` could not survive a rename whose whole point
  // is retiring the word. Old links are mapped forward rather than dropped:
  // it costs one lookup, and a bookmark silently falling back to the
  // Submissions tab is a confusing way to find out a link died.
  const requestedTab = searchParams.get('tab');
  const tabFromUrl =
    requestedTab && requestedTab in LEGACY_TAB_IDS
      ? LEGACY_TAB_IDS[requestedTab]
      : requestedTab;
  const validTabIds = tabs.map(tab => tab.id);
  const defaultTab = 'submissions';
  const activeTab =
    tabFromUrl && validTabIds.includes(tabFromUrl) ? tabFromUrl : defaultTab;

  const handleTabChange = useCallback(
    (tabId: string) => {
      setSearchParams({ tab: tabId });
    },
    [setSearchParams]
  );

  const renderTabContent = useCallback(() => {
    if (!study) return null;

    if (activeTab === 'submissions') {
      return (
        <div className="space-y-6">
          <ScopedSubmissionsTable
            ownerId={study.id}
            submissionScope="study"
            title="Study Level Submissions"
            pageSize={STUDY_TAB_PAGE_SIZE}
            className="bg-white rounded-lg border border-gray-200 overflow-hidden"
          />
          {!isSingleSite && (
            <ScopedSubmissionsTable
              studyId={study.id}
              submissionScope="site"
              title="Site Level Submissions"
              pageSize={STUDY_TAB_PAGE_SIZE}
              className="bg-white rounded-lg border border-gray-200 overflow-hidden"
            />
          )}
        </div>
      );
    }

    if (activeTab === 'sites') {
      return (
        <ScopedSitesTable
          studyId={study.id}
          variant={variant}
          pageSize={STUDY_TAB_PAGE_SIZE}
          className="bg-white rounded-lg border border-gray-200"
        />
      );
    }

    if (activeTab === 'documents') {
      return <StudyDocuments studyId={study.id} />;
    }

    if (activeTab === 'final-documents') {
      return <StudyFinalDocuments studyId={study.id} />;
    }

    if (activeTab === 'users' && canManageUsers) {
      return (
        <UserManagement
          resourcePath={`/studies/${study.id}`}
          canManageUsers={study.can_manage_users}
          refreshTrigger={usersRefreshTrigger}
          hierarchy="study"
          exportFilename={`study-${study.display_id ?? study.id}-users.csv`}
        />
      );
    }

    if (activeTab === 'activity') {
      return <StudyActivity studyId={study.id} />;
    }

    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="text-center py-12">
          <FileText className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            {tabs.find(tab => tab.id === activeTab)?.label} Content
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            This section is under development.
          </p>
        </div>
      </div>
    );
  }, [
    activeTab,
    study,
    variant,
    isSingleSite,
    canManageUsers,
    tabs,
    usersRefreshTrigger,
  ]);

  if (isLoading) {
    return (
      <div className="bg-white h-screen flex flex-col overflow-hidden">
        <div className="px-8 py-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex flex-col gap-1">
              <Skeleton className="h-5 w-48" />
              <Skeleton className="h-8 w-64" />
            </div>
            <div className="flex items-center gap-3">
              <Skeleton className="h-8 w-28" />
              <Skeleton className="h-8 w-28" />
              <Skeleton className="h-8 w-36" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !study) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="mx-auto max-w-md">
            <FileText className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              {error instanceof Error ? error.message : 'Study not found'}
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              The study you&apos;re looking for could not be loaded.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const variantBadge =
    variant === 'Single Site Protocol'
      ? 'Single site'
      : variant
        ? 'Multi site'
        : null;

  // See the header action-block comment below: Add Submission is only useful
  // once the study accepts non-site submission types too — otherwise it's a
  // second button that leads to the same place as New Site.
  const showAddSubmission =
    study.can_add_submissions && study.accepts_nested_submissions;

  const statusInfo = study.status ? getEntityStatusInfo(study.status) : null;

  const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return null;
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'numeric',
      day: 'numeric',
      year: '2-digit',
    });
  };

  const mappedAttributes = new Set(
    (study.mapped_fields ?? []).map(f => f.attribute)
  );

  const staticFields = [
    ...(!mappedAttributes.has('protocol_number')
      ? [{ label: 'Protocol Number', value: study.protocol_number }]
      : []),
    { label: 'Status', renderStatus: true },
    { label: 'Submitted', value: formatDate(study.submitted_at) },
    { label: 'Expires On', value: formatDate(study.expiration_date) },
    ...(!mappedAttributes.has('protocol_title') && study.protocol_title
      ? [{ label: 'Protocol Title', value: study.protocol_title }]
      : []),
    ...(!mappedAttributes.has('sponsor_name') && study.sponsor_name
      ? [{ label: 'Sponsor', value: study.sponsor_name }]
      : []),
    ...(!mappedAttributes.has('cro_name') && study.cro_name
      ? [{ label: 'CRO', value: study.cro_name }]
      : []),
    ...(!mappedAttributes.has('pi_name') && study.pi_name
      ? [{ label: 'PI Name', value: study.pi_name }]
      : []),
  ];

  const mappedFields = (study.mapped_fields ?? []).map(f => ({
    label: f.label,
    value: f.value,
  }));

  const detailFields = [...staticFields, ...mappedFields];

  return (
    <div className="bg-white h-screen flex flex-col overflow-hidden">
      {/* Page Header */}
      <div className="px-8 py-6 border-b border-gray-200">
        <div className="flex flex-wrap items-center justify-between gap-y-3">
          <div className="flex flex-col gap-1">
            <Breadcrumbs
              items={[
                {
                  label: breadcrumbLabel(
                    'Study',
                    study.display_id || study.protocol_number
                  ),
                  path: null,
                },
              ]}
            />
            <h1 className="text-gray-900 flex items-center gap-3">
              <span className="font-semibold text-2xl">
                {study.display_id ||
                  study.protocol_number ||
                  study.protocol_title ||
                  'Study Details'}
              </span>
              {variantBadge && (
                <span className="text-xs font-medium px-2.5 py-1 rounded-full text-gray-600 border border-gray-300">
                  {variantBadge}
                </span>
              )}
            </h1>
          </div>

          {/* Up to 4 actions can render here (Invite Users / Invite or Attach
              Site / Add Submission / New Site) — flex-wrap on the parent lets
              this group drop to its own line on narrow viewports instead of
              squeezing or overflowing the header. Add Submission renders
              before New Site so it keeps the rightmost/primary-CTA position
              whenever both show. Add Submission is hidden when the study
              isn't accepting nested submissions yet (not Active) — in that
              state New Site/Investigator is the ONLY category the wizard
              would offer, so a separate "Add Submission" button would just
              duplicate "New Site". When Add Submission is hidden, New Site
              is the last button in the group and takes over the primary
              styling/position instead. */}
          <div className="flex flex-wrap items-center gap-3">
            {canManageUsers && (
              <button
                onClick={handleInviteUsers}
                className="flex items-center gap-2 px-4 py-2.5 text-gray-600 hover:text-gray-900 transition-colors cursor-pointer text-sm"
              >
                <Users className="w-4 h-4" />
                <span>Invite Users</span>
              </button>
            )}

            {canManageUsers && study.accepts_more_sites && (
              <button
                onClick={handleConnectSite}
                className="flex items-center gap-2 px-4 py-2.5 text-gray-600 hover:text-gray-900 transition-colors cursor-pointer text-sm"
              >
                <Link2 className="w-4 h-4" />
                <span>Invite or Attach Site</span>
              </button>
            )}

            {study.can_add_submissions && study.accepts_more_sites && (
              <button
                onClick={handleCreateSite}
                className={
                  showAddSubmission
                    ? 'flex items-center gap-2 px-4 py-2.5 text-gray-600 hover:text-gray-900 transition-colors cursor-pointer text-sm'
                    : 'flex items-center gap-2 bg-primary hover:bg-primary-hover text-white text-sm font-medium px-3 py-1.5 rounded-md transition-colors cursor-pointer'
                }
              >
                <Plus className="w-4 h-4" />
                <span>New Site</span>
              </button>
            )}

            {showAddSubmission && (
              <button
                onClick={handleCreateSubmission}
                className="flex items-center gap-2 bg-primary hover:bg-primary-hover text-white text-sm font-medium px-3 py-1.5 rounded-md transition-colors cursor-pointer"
              >
                <Plus className="w-4 h-4" />
                <span>Add Submission</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Body: scrollable */}
      <div className="flex-1 overflow-y-auto">
        {/* Study Details card */}
        <div className="px-8 pt-8 pb-6">
          <div className="bg-white rounded-lg border border-gray-200">
            <div className="p-10">
              <h3 className="text-lg font-semibold text-gray-900 mb-6">
                Study Details
              </h3>
              <div className="grid grid-cols-4 gap-y-6 gap-x-12">
                {detailFields.map(field => (
                  <div key={field.label}>
                    <p className="text-xs font-medium text-gray-500 mb-2">
                      {field.label}
                    </p>
                    {field.renderStatus ? (
                      statusInfo ? (
                        <div className="flex items-center gap-2">
                          <div
                            className="w-2 h-2 rounded-full"
                            style={{ backgroundColor: statusInfo.color }}
                          />
                          <span className="text-gray-900 text-sm">
                            {statusInfo.label}
                          </span>
                        </div>
                      ) : (
                        <p className="text-gray-900 text-sm">-</p>
                      )
                    ) : (
                      <p className="text-gray-900 text-sm leading-relaxed">
                        {field.value || '-'}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 pt-6 px-8">
          <div className="flex gap-1">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`px-4 py-3 text-sm font-medium transition-colors border-b-2 cursor-pointer ${
                  activeTab === tab.id
                    ? 'border-primary text-primary'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab content */}
        <div className="px-8 pt-8 pb-8">{renderTabContent()}</div>
      </div>

      <BulkInviteUsersModal
        isOpen={isInviteModalOpen}
        onClose={handleCloseInviteModal}
        bulkInvitationsPath={`${study.show_path}/bulk_invitations`}
        assignableRolesPath={study.show_path}
        onSuccess={handleInvitationSuccess}
      />

      <ConnectSiteModal
        isOpen={isConnectSiteModalOpen}
        onClose={handleCloseConnectSiteModal}
        onSuccess={handleConnectSiteSuccess}
        studyId={study.id}
        studySiteEapSubCategory={study.site_eap_sub_category}
      />
    </div>
  );
};

export default StudyShow;
